import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, CloudCog, Droplets, FlaskConical, Ruler, Wind } from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import { Reveal, SectionTag } from "../components/bits";
import { ARTICLES, DISTANCES } from "../data/guides";
import { AUTHOR } from "../data/business";

const ICONS = { BookOpen, CloudCog, Droplets, FlaskConical, Ruler, Wind };

export default function GuideArticle() {
  const { slug } = useParams();
  const idx = ARTICLES.findIndex((a) => a.slug === slug);
  const article = ARTICLES[idx];

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center" data-testid="guide-not-found">
        <h1 className="font-display text-3xl font-bold text-[#F0FDF4]">Guide not found</h1>
        <Link to="/guides" className="rounded-full bg-[#39FF14] px-6 py-3 font-display text-sm font-bold text-[#050B07]">
          All grow guides
        </Link>
      </div>
    );
  }

  const Icon = ICONS[article.icon] || BookOpen;
  const prev = ARTICLES[idx - 1];
  const next = ARTICLES[idx + 1];

  return (
    <div className="mx-auto max-w-3xl px-5 pb-28 pt-28 sm:px-8" data-testid="guide-article">
      <SEO
        title={`${article.title} | SuperTechLED Grow Guides`}
        description={article.description}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.description,
          author: AUTHOR.name ? { "@type": "Person", name: AUTHOR.name } : { "@type": "Organization", name: "SuperTechLED" },
          publisher: { "@type": "Organization", name: "SuperTechLED" },
          mainEntityOfPage: SITE_URL + window.location.pathname,
        }}
      />
      <Reveal>
        <Link to="/guides" data-testid="guide-back-link" className="inline-flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#86EFAC] hover:text-[#39FF14]">
          <ArrowLeft className="h-3.5 w-3.5" /> Grow guides
        </Link>
        <div className="mt-8 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#39FF14]/25 bg-[#0A150F]">
            <Icon className="h-5 w-5 text-[#39FF14]" />
          </span>
          <div>
            <div className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#4ADE80]">{`${article.chapter} · ${article.kind}`}</div>
            <div className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#4ADE80]/60">{article.date}</div>
          </div>
        </div>
        <h1 className="font-display mt-5 text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl lg:text-5xl" data-testid="guide-title">
          {article.title}
        </h1>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-8 space-y-5 text-base leading-relaxed text-[#9FB8A8] sm:text-lg" data-testid="guide-body">
          {(article.table ? article.paragraphs.slice(0, 1) : article.paragraphs).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {article.table && (
          <>
            <div className="glass mt-8 overflow-hidden rounded-2xl" data-testid="guide-distance-table">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-[#39FF14]/15 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]">
                    <th className="px-6 py-4">Series</th>
                    <th className="px-6 py-4">Veg stage</th>
                    <th className="px-6 py-4">Flowering</th>
                  </tr>
                </thead>
                <tbody>
                  {DISTANCES.map((d) => (
                    <tr key={d.series} className="border-b border-[#39FF14]/8 last:border-0">
                      <td className="px-6 py-4 font-semibold text-[#F0FDF4]">{d.series}</td>
                      <td className="px-6 py-4 text-[#C9DCD0]">{d.veg}</td>
                      <td className="px-6 py-4 text-[#C9DCD0]">{d.bloom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-[#9FB8A8] sm:text-lg">
              {article.paragraphs.slice(1).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </>
        )}

        {article.linkTo && (
          <p className="mt-6 text-base sm:text-lg">
            <Link to={`/guides/${article.linkTo}`} className="font-semibold text-[#39FF14] underline underline-offset-4 hover:text-[#86EFAC]">
              See the full hanging-distance chart →
            </Link>
          </p>
        )}

        {AUTHOR.name && (
          <div className="mt-12 flex items-start gap-4 rounded-2xl border border-[#39FF14]/15 bg-[#071009] p-6" data-testid="author-bio">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#39FF14]/25 bg-[#0A150F] font-display text-sm font-bold text-[#39FF14]">
              {AUTHOR.name.split(" ").map((w) => w[0]).join("")}
            </div>
            <div>
              <div className="font-display text-sm font-bold text-[#F0FDF4]">{`Written by ${AUTHOR.name}`}</div>
              {AUTHOR.bioShort && <p className="mt-1 text-sm leading-relaxed text-[#9FB8A8]">{AUTHOR.bioShort}</p>}
            </div>
          </div>
        )}
      </Reveal>

      <Reveal delay={0.15}>
        <div className="glass mt-14 rounded-2xl p-6">
          <SectionTag>Keep reading</SectionTag>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {prev ? (
              <Link to={`/guides/${prev.slug}`} data-testid="guide-prev" className="group rounded-xl border border-[#39FF14]/15 bg-[#0A150F] p-4 transition-colors hover:border-[#39FF14]/50">
                <div className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#4ADE80]">Previous</div>
                <div className="font-display mt-1 font-bold text-[#F0FDF4] group-hover:text-[#39FF14]">{prev.title}</div>
              </Link>
            ) : <div />}
            {next && (
              <Link to={`/guides/${next.slug}`} data-testid="guide-next" className="group rounded-xl border border-[#39FF14]/15 bg-[#0A150F] p-4 text-right transition-colors hover:border-[#39FF14]/50">
                <div className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#4ADE80]">Next</div>
                <div className="font-display mt-1 font-bold text-[#F0FDF4] group-hover:text-[#39FF14]">{next.title}</div>
              </Link>
            )}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.2} className="mt-10 text-center">
        <Link
          to="/shop"
          data-testid="guide-shop-button"
          className="group inline-flex items-center gap-2 rounded-full bg-[#39FF14] px-8 py-4 font-display text-sm font-bold text-[#050B07] transition-shadow hover:shadow-[0_0_36px_rgba(57,255,20,0.45)]"
        >
          Shop the lights these guides use
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  );
}
